import React from "react";
import { FaDove, FaShieldAlt, FaTree, FaBlog, FaQuestionCircle, FaBug } from "react-icons/fa";
import { GiBrickWall, GiFactory, GiPoolDive, GiMonkey } from "react-icons/gi";
import { MdPrivacyTip, MdPolicy, MdPets, MdConstruction } from "react-icons/md";

import FooterCompanyInfo from "./FooterCompantInfo";
import FooterSection from "./FooterSection";
import FooterBottom from "./FooterBottom";
import { BUSINESS } from "../config/business";

interface FooterItem {
  name: string;
  icon: React.ReactNode;
  link: string;
}

const pigeonBirdNets: FooterItem[] = [
  { name: "Pigeon Safety Nets", icon: <FaDove />, link: "/services/pigeon-safety-nets-in-bangalore" },
  { name: "Anti Bird Nets", icon: <FaDove />, link: "/services/anti-bird-nets-in-bangalore" },
  { name: "Bird Nets for Balconies", icon: <FaDove />, link: "/services/balcony-bird-nets-in-bangalore" },
  { name: "Bird Netting Service", icon: <FaDove />, link: "/services/anti-bird-nets-in-bangalore" },
  { name: "Bird Protection Nets", icon: <FaDove />, link: "/services/anti-bird-nets-in-bangalore" },
  { name: "Pigeon Nets Installation", icon: <FaDove />, link: "/services/pigeon-safety-nets-in-bangalore" },
  { name: "Pigeon and Bird Spikes", icon: <FaDove />, link: "/services/bird-spikes-in-bangalore" },
  { name: "Shade Nets", icon: <FaShieldAlt />, link: "/services/shade-nets-in-bangalore" },
  { name: "Nylon Nets", icon: <FaShieldAlt />, link: "/services/nylon-safety-nets-in-bangalore" },
  { name: "Anti Seagull Nets", icon: <FaShieldAlt />, link: "/services/anti-bird-nets-in-bangalore" },
  { name: "Glass Safety Nets", icon: <FaShieldAlt />, link: "/services/glass-protection-safety-nets-in-bangalore" },
  { name: "HDPE Nets", icon: <FaShieldAlt />, link: "/services/hdpe-safety-nets-in-bangalore" },
  { name: "Window Safety Nets", icon: <FaShieldAlt />, link: "/services/window-safety-nets-in-bangalore" },
];

const safetyNets: FooterItem[] = [
  { name: "Building Safety Nets", icon: <GiBrickWall />, link: "/services/building-covering-safety-nets-in-bangalore" },
  { name: "Car Parking Safety Nets", icon: <FaShieldAlt />, link: "/services/car-parking-safety-nets-in-bangalore" },
  { name: "Children Safety Nets", icon: <FaShieldAlt />, link: "/services/children-safety-nets-in-bangalore" },
  { name: "Construction Safety Nets", icon: <FaShieldAlt />, link: "/services/construction-safety-nets-in-bangalore" },
  { name: "Industrial Safety Nets", icon: <GiFactory />, link: "/services/industrial-safety-nets-in-bangalore" },
  { name: "Pets Safety Nets", icon: <MdPets />, link: "/services/pet-safety-nets-in-bangalore" },
  { name: "Swimming Pool Safety Nets", icon: <GiPoolDive />, link: "/services/swimming-pool-safety-nets-in-bangalore" },
  { name: "Duct Area Safety Nets", icon: <MdConstruction />, link: "/services/duct-area-safety-nets-in-bangalore" },
  { name: "Open Area Safety Nets", icon: <GiBrickWall />, link: "/services/open-area-safety-nets-in-bangalore" },
  { name: "Mosquito Safety Nets", icon: <FaBug />, link: "/services/mosquito-mesh-in-bangalore" },
  { name: "Monkey Safety Nets", icon: <GiMonkey />, link: "/services/monkey-safety-nets-in-bangalore" },
  { name: "Coconut Tree Safety Nets", icon: <FaTree />, link: "/services/coconut-tree-safety-nets-in-bangalore" },
];

const resources: FooterItem[] = [
  { name: "Blog", icon: <FaBlog />, link: "/blogs" },
  { name: "FAQs", icon: <FaQuestionCircle />, link: "/faq" },
  { name: "Privacy Policy", icon: <MdPrivacyTip />, link: "/privacy-policy" },
  { name: "Terms & Conditions", icon: <MdPolicy />, link: "/terms-and-conditions" },
];


const Footer: React.FC = () => {
  return (
    <footer className="relative overflow-hidden bg-[#082F49] text-white/75" role="contentinfo">

      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0C4A6E] to-[#082F49]" />

      {/* Glow */}
      <div className="absolute left-1/2 top-[-120px] h-[300px] w-[600px] -translate-x-1/2 bg-[#7DD3FC]/15 blur-[120px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-16">

        {/* Company Info */}
        <FooterCompanyInfo />

        {/* Divider */}
        <div className="my-10 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        {/* Sections */}
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-3">
          <FooterSection
            title="Pigeon & Bird Nets"
            items={pigeonBirdNets}
            iconColor="text-[#7DD3FC]"
          />
          <FooterSection
            title="Safety Nets"
            items={safetyNets}
            iconColor="text-[#7DD3FC]"
          />
          <FooterSection
            title="Resources"
            items={resources}
            iconColor="text-[#BAE6FD]"
          />
        </div>

        <div
          className="mt-12 rounded-2xl border border-[#7DD3FC]/25 bg-white/[0.06] px-5 py-6 shadow-[0_16px_45px_rgba(2,6,23,0.18)] sm:px-7"
          aria-label="Related website"
        >
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#7DD3FC]">
            Our related safety net website
          </p>
          <p className="mt-3 max-w-5xl text-sm leading-7 text-white/70 sm:text-base">
            Servani Safety Nets primarily handles Bengaluru enquiries. For customers researching balcony safety nets, pigeon safety nets, invisible grills and related property protection services in other South Indian locations, visit our related website{" "}
            <a
              href="https://eversafesafetynets.com/"
              className="font-extrabold text-[#BAE6FD] underline decoration-[#7DD3FC] decoration-2 underline-offset-4 transition hover:text-white focus-visible:rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7DD3FC]"
            >
              EverSafe Safety Nets &amp; Invisible Grills
            </a>
            . The EverSafe website presents its own coverage, contact details and quotation process. Availability, pricing and service terms are confirmed separately through each website.
          </p>
        </div>

        {/* CTA Strip (HIGH CONVERSION) */}
        <div className="mt-16 text-center">
          <p className="mb-4 text-lg text-white/65">
            Need help choosing the right safety solution?
          </p>

          <a
            href={BUSINESS.phoneUrl}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full
            bg-[#0369A1] hover:bg-[#075985]
            text-white font-semibold
            shadow-lg hover:shadow-sky-950/40
            hover:scale-105 transition-all duration-300"
          >
            📞 Get Free Consultation
          </a>
        </div>

        {/* Bottom Divider */}
        <div className="my-10 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        <FooterBottom />
      </div>
    </footer>
  );
};

export default Footer;
